import type { MarkType, NodeType } from "prosemirror-model";
import type { EditorState } from "prosemirror-state";
import { isInTable } from "prosemirror-tables";

import { focusSchema } from "./schema";

export type EditorToolbarState = {
  blockquote: boolean;
  bulletList: boolean;
  code: boolean;
  codeBlock: boolean;
  em: boolean;
  headingLevel: number | null;
  link: boolean;
  orderedList: boolean;
  strike: boolean;
  strong: boolean;
  table: boolean;
};

export const emptyToolbarState: EditorToolbarState = {
  blockquote: false,
  bulletList: false,
  code: false,
  codeBlock: false,
  em: false,
  headingLevel: null,
  link: false,
  orderedList: false,
  strike: false,
  strong: false,
  table: false,
};

const selectionHasMark = (state: EditorState, markType: MarkType) => {
  const { empty, from, to, $from } = state.selection;

  if (empty) {
    return Boolean(markType.isInSet(state.storedMarks ?? $from.marks()));
  }

  return state.doc.rangeHasMark(from, to, markType);
};

const hasNodeAncestor = (state: EditorState, nodeType: NodeType) => {
  const { $from } = state.selection;

  for (let depth = $from.depth; depth > 0; depth--) {
    if ($from.node(depth).type === nodeType) {
      return true;
    }
  }

  return false;
};

const findHeadingLevel = (state: EditorState) => {
  const { $from } = state.selection;

  for (let depth = $from.depth; depth > 0; depth--) {
    const node = $from.node(depth);

    if (node.type === focusSchema.nodes.heading) {
      return Number(node.attrs.level) || null;
    }
  }

  return null;
};

export const getEditorToolbarState = (
  state: EditorState,
): EditorToolbarState => ({
  blockquote: hasNodeAncestor(state, focusSchema.nodes.blockquote),
  bulletList: hasNodeAncestor(state, focusSchema.nodes.bullet_list),
  code: selectionHasMark(state, focusSchema.marks.code),
  codeBlock: hasNodeAncestor(state, focusSchema.nodes.code_block),
  em: selectionHasMark(state, focusSchema.marks.em),
  headingLevel: findHeadingLevel(state),
  link: selectionHasMark(state, focusSchema.marks.link),
  orderedList: hasNodeAncestor(state, focusSchema.nodes.ordered_list),
  strike: selectionHasMark(state, focusSchema.marks.strike),
  strong: selectionHasMark(state, focusSchema.marks.strong),
  table: isInTable(state),
});

export const equalToolbarStates = (
  left: EditorToolbarState,
  right: EditorToolbarState,
) =>
  left.blockquote === right.blockquote &&
  left.bulletList === right.bulletList &&
  left.code === right.code &&
  left.codeBlock === right.codeBlock &&
  left.em === right.em &&
  left.headingLevel === right.headingLevel &&
  left.link === right.link &&
  left.orderedList === right.orderedList &&
  left.strike === right.strike &&
  left.strong === right.strong &&
  left.table === right.table;
